'use client';

import React, { useState } from 'react';
import { ResetPasswordIcon } from '../../Icons/Icons';
import { AuthModalFooter, RegisterPrompt } from '../common/AuthModalFooter';
import { ModalCloseButton } from '../../common/modal-close-button';
import { DialogTitle } from '../../ui/dialog';
import { useLoginModals } from '../common/LoginModalsManager';
import { resetPasswordWithToken, authErrorMessage } from '@/lib/auth/common/client';
import logout from '@/utils/auth/common/logout';
import { deleteQueryParam } from '@/lib/utils/url-params';
import { PasswordField } from './PasswordField';

interface ResetPasswordWithTokenModalProps {
  onClose?: () => void;
  token: string;
}

/**
 * Rendered only while open (mounted inside a closed-by-default CommonModal), so fields
 * reset by unmounting — no `isOpen` prop/early-return needed.
 */
export function ResetPasswordWithTokenModal({
  onClose,
  token,
}: ResetPasswordWithTokenModalProps) {
  const loginModals = useLoginModals();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    deleteQueryParam('resetToken');
    onClose?.();
  };

  const handleReturnToLogin = () => {
    deleteQueryParam('resetToken');
    onClose?.();
    // Delay so this modal closes before the login modal opens
    setTimeout(() => {
      loginModals.openLoginModal();
    }, 100);
  };

  const handleResetPassword = async () => {
    setErrors({});
    setErrorMsg('');
    const newErrors: { password?: string; confirmPassword?: string } = {};

    if (!password) {
      newErrors.password = 'Password is required';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirm Password is required';
    }

    if (!password && !confirmPassword) {
      newErrors.password = 'All fields are required';
      newErrors.confirmPassword = 'All fields are required';
    }

    if (password && confirmPassword && password !== confirmPassword) {
      newErrors.password = 'Passwords do not match';
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (password && password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!token) {
      setErrorMsg('Invalid reset link. Please request a new password reset.');
      return;
    }

    setLoading(true);
    try {
      const { ok, body } = await resetPasswordWithToken({ token, password });

      if (ok) {
        // End any live session under the old password before letting the user sign in again.
        await logout();
        // Old '/login?resetpassword=success' route no longer exists; go straight to the login modal instead.
        handleReturnToLogin();
      } else {
        // Surface the API's real reason (expired/used token, same password) instead of a generic message.
        setErrorMsg(authErrorMessage(body, 'Failed to reset password. Please try again.'));
      }
    } catch {
      // Only network/DNS failures land here — a rejected reset resolves as ok:false above.
      setErrorMsg('Unable to reach the server. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleResetPassword();
    }
  };

  const clearErrors = () => {
    setErrors({});
    setErrorMsg('');
  };

  return (
    <div className="login-modal-container">
      <ModalCloseButton onClick={handleClose} />
      <div className="login-modal-content">
        <DialogTitle className="cs-h3 tab:h1-compact mb-6">Reset Password</DialogTitle>
        

        <div className="flex flex-col gap-6">
          {/* Password Field */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="password"
              className="cs-input-field flex items-center justify-between"
            >
              <span>Password</span>
              <span className="cs-asterisk">*</span>
            </label>
            <PasswordField
              id="password"
              placeholder="Enter your new password"
              value={password}
              onChange={(value) => {
                setPassword(value);
                clearErrors();
              }}
              onFocus={clearErrors}
              onKeyUp={handleKeyPress}
              hasError={!!errors.password}
              className="!bg-surface-3"
            />
          </div>

          {/* Confirm Password Field */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="confirmPassword"
              className="cs-input-field flex items-center justify-between"
            >
              <span>Confirm Password</span>
              <span className="cs-asterisk">*</span>
            </label>
            <PasswordField
              id="confirmPassword"
              placeholder="Confirm your new password"
              value={confirmPassword}
              onChange={(value) => {
                setConfirmPassword(value);
                clearErrors();
              }}
              onFocus={clearErrors}
              onKeyUp={handleKeyPress}
              hasError={!!errors.confirmPassword}
              className="!bg-surface-3"
            />
          </div>

          <RegisterPrompt />

          <AuthModalFooter
            onCancel={handleReturnToLogin}
            onSubmit={handleResetPassword}
            loading={loading}
            submitLabel="Reset Password"
            loadingLabel="Resetting Password..."
            submitIcon={!loading && <ResetPasswordIcon className="text-onAction-strong" />}
            error={errorMsg || errors.password || errors.confirmPassword}
          />
        </div>
      </div>
    </div>
  );
}
